import { PUBLIC_ENV } from "@/config/publicEnv";
import { recordAttribution } from "@/lib/attribution";

let initialized = false;

/**
 * Loads Google Tag Manager from runtime configuration.
 *
 * Must be called AFTER `loadPublicEnv()` resolves — the container id comes from GET /api/config,
 * not a bundle literal, so calling at import time would run with an empty value.
 *
 * The container id (GTM_CONTAINER_ID) is set in production, and on staging for GTM Preview checks.
 * With no id (dev, local) this NO-OPS: GTM never loads and no request to googletagmanager.com is made.
 *
 * This is the standard GTM install expressed in code — the copy-paste `<head>` snippet becomes the
 * dataLayer bootstrap + async gtm.js injection below (placed as high in `<head>` as we can at
 * runtime); the `<body>` `<noscript>` fallback is injected too for parity with the standard install
 * (inert in this JS-rendered SPA, but harmless and future-proof).
 */
export function initGtm(): void {
  if (initialized) return;

  const id = PUBLIC_ENV.GTM_CONTAINER_ID;
  if (!id) return; // No container id → GTM disabled (dev/local); render nothing.

  const src = encodeURIComponent(id);

  // Before the GTM snippet, and under the same gate, per GFG-424.
  recordAttribution();

  // <head> snippet: initialise dataLayer and inject gtm.js as high in <head> as we can at runtime.
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${src}`;
  document.head.prepend(script);

  // <body> noscript fallback, immediately after the opening <body>.
  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${src}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.prepend(noscript);

  initialized = true;
}
